import {InfoTooltip} from '@wandb/components';
import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {type MediaString} from '@wandb/weave/common/types/media';
import {isUndefined} from 'lodash';
import type {CSSProperties} from 'react';
import * as React from 'react';

import {useDetectPanelVariant} from './Panel/hooks/hooks';
import {useStablePanelId} from './Panel/PanelIDContext';

// Force using basic display if height is less than this value
const HEIGHT_MAX_REQUIRE_BASIC = 75;
// Include the details box if height is greater than this value
const HEIGHT_MIN_INCLUDE_DETAILS = 135;

const PROPERTY_BOX_CONTAINER_CLASSES =
  'flex flex-col flex-1 w-full h-min min-w-0 p-8 gap-2 bg-[light-dark(var(--moon-100),var(--moon-850))] rounded-sm text-xs text-wb-text-tertiary';
const PROPERTY_BOX_ROW_CLASSES =
  'flex flex-row gap-4 justify-start items-start min-w-0 w-full max-w-full truncate';
const PROPERTY_BOX_LABEL_CLASSES = 'font-semibold flex-shrink-0';
const PROPERTY_BOX_VALUE_CLASSES = 'min-w-0 truncate';

interface MessageMediaNotFoundProps {
  runName?: string;
  stepIndex?: number;
  mediaKey?: string;
  mediaIndex?: number;
  mediaType?: MediaString;
  basic?: boolean; // If true, shows a question mark icon with the message as a popup
  maxWidth?: CSSProperties['maxWidth'];
  height?: CSSProperties['height'];
}

interface MessageMediaNotFoundPropertiesBoxRow {
  label: string;
  value: string | number | undefined;
}

const MessageMediaNotFoundPropertiesBoxRow = (
  props: MessageMediaNotFoundPropertiesBoxRow
) => {
  const {label, value} = props;
  return (
    <div className={PROPERTY_BOX_ROW_CLASSES}>
      <span className={PROPERTY_BOX_LABEL_CLASSES}>{label}</span>
      <span className={PROPERTY_BOX_VALUE_CLASSES}>{value}</span>
    </div>
  );
};

const MessageMediaNotFoundPropertiesBox = (
  props: MessageMediaNotFoundProps
) => {
  const {runName, stepIndex, mediaKey, mediaIndex} = props;
  const rows: MessageMediaNotFoundPropertiesBoxRow[] = [
    {label: 'Media Key:', value: mediaKey},
    {label: 'Run:', value: runName},
    {label: 'Step:', value: stepIndex},
    {label: 'Index:', value: mediaIndex},
  ];
  return (
    <div className={PROPERTY_BOX_CONTAINER_CLASSES}>
      {rows.map(row => (
        <MessageMediaNotFoundPropertiesBoxRow key={row.label} {...row} />
      ))}
    </div>
  );
};

const MessageMediaNotFound = (props: MessageMediaNotFoundProps) => {
  const stablePanelId = useStablePanelId();
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const {stepIndex, mediaKey, mediaIndex, basic, maxWidth, height} = props;
  const isHeightNumeric = height && typeof height === 'number';
  const shouldForceBasicDisplay =
    isHeightNumeric && height < HEIGHT_MAX_REQUIRE_BASIC;
  const shouldIncludeDetails =
    isHeightNumeric && height >= HEIGHT_MIN_INCLUDE_DETAILS;
  let mediaType: string = props.mediaType || 'media';
  if (mediaType === 'table') {
    mediaType = 'tables';
  } else if (mediaType === 'object3D') {
    mediaType = '3D objects';
  }
  const textSizingClasses = isFullScreenPanelPage ? 'text-md/8' : 'text-xs';
  const errorMessage = (
    <PanelError
      panelID={stablePanelId}
      panelViewType="Media Browser"
      mediaType={mediaType}
      className="media-missing"
      message={
        <div
          className="flex flex-col flex-1 gap-4 w-full min-w-0 items-center justify-center"
          style={{maxWidth}}>
          <div
            className={`flex justify-center items-center text-center text-wb-text-secondary font-semibold ${textSizingClasses}`}>
            No matching media
          </div>
          {isFullScreenPanelPage && shouldIncludeDetails && (
            <div className="props flex flex-1 px-4 md:px-8 lg:px-16 w-full min-w-0">
              <MessageMediaNotFoundPropertiesBox {...props} />
            </div>
          )}
        </div>
      }
      logErrorMessage={`This run didn't log ${mediaType || 'media'}${mediaKey ? ` for key "${mediaKey}", step ${stepIndex}` : ''}${isUndefined(mediaIndex) ? '' : `, index ${mediaIndex}`}.`}
    />
  );
  return (
    <div
      className="flex flex-col flex-1 gap-4 w-full min-w-0 items-center justify-center"
      style={{maxWidth}}>
      {basic || shouldForceBasicDisplay ? (
        <InfoTooltip
          variant="info"
          size="md"
          id="media-not-found-tooltip"
          aria-label="Media not found">
          No matching media
        </InfoTooltip>
      ) : (
        errorMessage
      )}
    </div>
  );
};

export default MessageMediaNotFound;
